import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../ToastContext';

// ── Cloud Function references ────────────────────────────────────────────────
const joinTableFn = httpsCallable(functions, 'joinPokerTable');
const leaveTableFn = httpsCallable(functions, 'leavePokerTable');
const pokerActionFn = httpsCallable(functions, 'pokerAction');
const pokerAdvanceFn = httpsCallable(functions, 'pokerAdvance');
const pokerRebuyFn = httpsCallable(functions, 'pokerRebuy');

// ── Test Cloud Function references ───────────────────────────────────────────
const testAddBotFn = httpsCallable(functions, 'pokerTestAddBot');
const testBotActionFn = httpsCallable(functions, 'pokerTestBotAction');
const testRemoveBotFn = httpsCallable(functions, 'pokerTestRemoveBot');
const testResetFn = httpsCallable(functions, 'pokerTestReset');

// ── CSS Keyframe Animations (injected once) ──────────────────────────────────
const ANIM_STYLE_ID = 'poker-animations';
if (typeof document !== 'undefined' && !document.getElementById(ANIM_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = ANIM_STYLE_ID;
  style.textContent = `
    @keyframes pk-dealIn {
      0% { opacity:0; transform:translate(-50%,-50%) scale(0.3) rotate(-15deg); }
      60% { opacity:1; transform:translate(0,0) scale(1.08) rotate(2deg); }
      100% { opacity:1; transform:translate(0,0) scale(1) rotate(0deg); }
    }
    @keyframes pk-cardFlip {
      0% { transform:perspective(400px) rotateY(90deg) scale(0.8); opacity:0.5; }
      50% { transform:perspective(400px) rotateY(-10deg) scale(1.05); opacity:1; }
      100% { transform:perspective(400px) rotateY(0deg) scale(1); opacity:1; }
    }
    @keyframes pk-foldOut {
      0% { opacity:1; transform:scale(1) rotate(0deg); }
      100% { opacity:0; transform:scale(0.5) rotate(-20deg) translateY(-20px); }
    }
    @keyframes pk-chipSlide {
      0% { opacity:0; transform:translateY(0) scale(0.5); }
      50% { opacity:1; transform:translateY(-8px) scale(1.1); }
      100% { opacity:1; transform:translateY(0) scale(1); }
    }
    @keyframes pk-allInPulse {
      0%,100% { box-shadow:0 0 10px rgba(255,111,0,0.4), 0 0 20px rgba(255,111,0,0.2); }
      50% { box-shadow:0 0 25px rgba(255,111,0,0.8), 0 0 50px rgba(255,111,0,0.4), 0 0 80px rgba(255,111,0,0.2); }
    }
    @keyframes pk-allInText {
      0% { opacity:0; transform:translate(-50%,-50%) scale(0.3); }
      30% { opacity:1; transform:translate(-50%,-50%) scale(1.3); }
      60% { opacity:1; transform:translate(-50%,-50%) scale(1); }
      100% { opacity:0; transform:translate(-50%,-50%) scale(1.1) translateY(-20px); }
    }
    @keyframes pk-winGlow {
      0%,100% { box-shadow:0 0 15px rgba(255,215,0,0.3); border-color:rgba(255,215,0,0.5); }
      50% { box-shadow:0 0 40px rgba(255,215,0,0.7), 0 0 80px rgba(255,215,0,0.3); border-color:rgba(255,215,0,0.9); }
    }
    @keyframes pk-winBanner {
      0% { opacity:0; transform:translate(-50%,-50%) scale(0.5); }
      20% { opacity:1; transform:translate(-50%,-50%) scale(1.15); }
      40% { transform:translate(-50%,-50%) scale(1); }
      100% { opacity:1; transform:translate(-50%,-50%) scale(1); }
    }
    @keyframes pk-particle {
      0% { opacity:1; transform:translate(0,0) scale(1); }
      100% { opacity:0; transform:translate(var(--px), var(--py)) scale(0); }
    }
    @keyframes pk-potPulse {
      0%,100% { transform:scale(1); }
      50% { transform:scale(1.08); }
    }
    @keyframes pk-turnPulse {
      0%,100% { box-shadow:0 0 8px rgba(76,175,80,0.4); }
      50% { box-shadow:0 0 20px rgba(76,175,80,0.8), 0 0 40px rgba(76,175,80,0.3); }
    }
    @keyframes pk-actionBadge {
      0% { opacity:0; transform:translateY(0) scale(0.7); }
      20% { opacity:1; transform:translateY(-8px) scale(1.1); }
      70% { opacity:1; transform:translateY(-14px) scale(1); }
      100% { opacity:0; transform:translateY(-24px) scale(0.9); }
    }
    @keyframes pk-screenFlash {
      0% { opacity:0.15; }
      100% { opacity:0; }
    }
    @keyframes pk-shimmer {
      0% { background-position:-200% center; }
      100% { background-position:200% center; }
    }
    @keyframes pk-fadeIn {
      0% { opacity:0; transform:translateY(6px); }
      100% { opacity:1; transform:translateY(0); }
    }
    @keyframes pk-myCardReveal {
      0% { opacity:0; transform:translateY(30px) rotateX(20deg) scale(0.7); }
      50% { opacity:1; transform:translateY(-4px) rotateX(-3deg) scale(1.04); }
      100% { opacity:1; transform:translateY(0) rotateX(0deg) scale(1); }
    }
  `;
  document.head.appendChild(style);
}

// ── Sound Effects (Web Audio API) ────────────────────────────────────────────
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (_audioCtx.state === 'suspended') _audioCtx.resume();
  return _audioCtx;
}

function playTone(freq, duration, type = 'sine', vol = 0.12) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch (e) { /* audio not available */ }
}

function playNoise(duration, vol = 0.06) {
  try {
    const ctx = getAudioCtx();
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(4000, ctx.currentTime);
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    src.start(ctx.currentTime);
    src.stop(ctx.currentTime + duration);
  } catch (e) { /* audio not available */ }
}

function playChipStack(count = 3) {
  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      playNoise(0.03, 0.05);
      playTone(800 + Math.random() * 400, 0.04, 'sine', 0.04);
    }, i * 50);
  }
}

const SFX = {
  fold: () => {
    playTone(250, 0.12, 'triangle', 0.07);
    playNoise(0.06, 0.03);
  },
  check: () => {
    playTone(700, 0.05, 'sine', 0.1);
    setTimeout(() => playTone(700, 0.05, 'sine', 0.1), 60);
  },
  call: () => {
    playChipStack(2);
    playTone(500, 0.1, 'sine', 0.08);
  },
  raise: () => {
    playChipStack(4);
    playTone(440, 0.06, 'square', 0.05);
    setTimeout(() => playTone(660, 0.1, 'square', 0.05), 80);
  },
  allIn: () => {
    playChipStack(6);
    setTimeout(() => {
      playTone(440, 0.15, 'sawtooth', 0.1);
      setTimeout(() => playTone(554, 0.15, 'sawtooth', 0.1), 120);
      setTimeout(() => playTone(660, 0.15, 'sawtooth', 0.1), 240);
      setTimeout(() => playTone(880, 0.3, 'sawtooth', 0.12), 360);
    }, 100);
  },
  deal: () => {
    for (let i = 0; i < 3; i++) {
      setTimeout(() => playNoise(0.05, 0.07), i * 80);
    }
  },
  dealCard: (i = 0) => {
    setTimeout(() => playNoise(0.04, 0.06), i * 120);
  },
  win: () => {
    [523, 659, 784, 880, 1047].forEach((f, i) =>
      setTimeout(() => playTone(f, 0.25, 'sine', 0.12), i * 110)
    );
    setTimeout(() => playChipStack(8), 200);
  },
  timerWarn: () => {
    playTone(880, 0.06, 'square', 0.08);
    setTimeout(() => playTone(880, 0.06, 'square', 0.08), 100);
  },
  newHand: () => {
    playTone(330, 0.06, 'triangle', 0.05);
    setTimeout(() => playTone(440, 0.08, 'triangle', 0.06), 90);
    setTimeout(() => playTone(554, 0.06, 'triangle', 0.04), 170);
  },
  yourTurn: () => {
    playTone(660, 0.08, 'sine', 0.08);
    setTimeout(() => playTone(880, 0.12, 'sine', 0.1), 100);
  },
};

// ── Card rendering with animations ──────────────────────────────────────────
const SUIT_SYMBOL = { spade: '\u2660', heart: '\u2665', diamond: '\u2666', club: '\u2663' };
const SUIT_COLOR = { spade: '#e0e0e0', heart: '#ef5350', diamond: '#42a5f5', club: '#66bb6a' };

function CardView({ card, faceDown, large, animClass, animDelay, animDuration }) {
  const w = large ? 62 : 46;
  const h = large ? 88 : 66;
  const fontSize = large ? 16 : 12;
  const anim = animClass ? `${animClass} ${animDuration || '0.4s'} ease ${animDelay || '0s'} both` : undefined;

  if (faceDown || !card) {
    return (
      <div style={{
        width: w, height: h, borderRadius: 6,
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%)',
        border: '2px solid rgba(255,255,255,0.2)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        animation: anim,
      }}>
        <div style={{
          width: w - 10, height: h - 10, borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.15)',
          background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.03) 3px, rgba(255,255,255,0.03) 6px)',
        }} />
      </div>
    );
  }

  const symbol = SUIT_SYMBOL[card.suit] || card.suit;
  const color = SUIT_COLOR[card.suit] || '#fff';

  return (
    <div style={{
      width: w, height: h, borderRadius: 6,
      background: 'linear-gradient(180deg, #2e2e2e, #1a1a1a)',
      border: `2px solid ${color}50`,
      boxShadow: `0 2px 10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 8px ${color}15`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontSize, fontWeight: 'bold', color,
      flexShrink: 0, position: 'relative',
      animation: anim,
    }}>
      <div style={{ position: 'absolute', top: 3, left: 5, fontSize: fontSize - 1, lineHeight: 1, opacity: 0.6 }}>{card.rank}</div>
      <div style={{ fontSize: fontSize + 2, lineHeight: 1 }}>{card.rank}</div>
      <div style={{ fontSize: fontSize + 6, lineHeight: 1, marginTop: 2 }}>{symbol}</div>
      <div style={{ position: 'absolute', bottom: 3, right: 5, fontSize: fontSize - 1, lineHeight: 1, opacity: 0.6, transform: 'rotate(180deg)' }}>{card.rank}</div>
    </div>
  );
}

// ── Player position calculator ───────────────────────────────────────────────
function getPlayerPosition(index, total, myIndex) {
  const offset = myIndex >= 0 ? myIndex : 0;
  const adjusted = (index - offset + total) % total;
  const angle = (2 * Math.PI * adjusted / total) - Math.PI / 2;
  const rx = 43;
  const ry = 40;
  return {
    left: `${50 + rx * Math.cos(angle)}%`,
    top: `${50 + ry * Math.sin(angle)}%`,
  };
}

// ── Timer bar component ──────────────────────────────────────────────────────
function TimerBar({ deadline }) {
  const [pct, setPct] = useState(100);
  useEffect(() => {
    if (!deadline) { setPct(100); return; }
    const interval = setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      setPct((remaining / 30000) * 100);
    }, 50);
    return () => clearInterval(interval);
  }, [deadline]);

  const color = pct > 50 ? '#4caf50' : pct > 20 ? '#ff9800' : '#f44336';
  return (
    <div style={{
      width: '100%', height: 4, background: 'rgba(255,255,255,0.1)',
      borderRadius: 2, overflow: 'hidden', marginTop: 4,
    }}>
      <div style={{
        width: `${pct}%`, height: '100%', background: color,
        transition: 'width 0.1s linear, background 0.3s',
        borderRadius: 2,
      }} />
    </div>
  );
}

// ── Action Badge (floating text like "FOLD", "CALL 2.0") ────────────────────
function ActionBadge({ action, amount, visible }) {
  if (!visible || !action) return null;
  const colors = {
    fold: '#f44336', check: '#42a5f5', call: '#2196f3',
    raise: '#4caf50', 'all-in': '#ff6f00',
  };
  const label = action === 'raise' && amount
    ? `RAISE ${amount.toFixed(2)}`
    : action.toUpperCase();

  return (
    <div style={{
      position: 'absolute', top: -28, left: '50%',
      transform: 'translateX(-50%)',
      animation: 'pk-actionBadge 1.2s ease-out both',
      fontSize: 11, fontWeight: 800, color: colors[action] || '#fff',
      textTransform: 'uppercase', letterSpacing: 1,
      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
      whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 20,
    }}>
      {label}
    </div>
  );
}

// ── Gold Particles (for win celebration) ─────────────────────────────────────
function GoldParticles({ active }) {
  const particles = useMemo(() => {
    return Array.from({ length: 16 }).map(() => ({
      px: `${(Math.random() - 0.5) * 120}px`,
      py: `${(Math.random() - 0.5) * 120 - 40}px`,
      size: 4 + Math.random() * 6,
      delay: Math.random() * 0.4,
      dur: 0.6 + Math.random() * 0.6,
    }));
  }, []);

  if (!active) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 30 }}>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: '50%', left: '50%',
          width: p.size, height: p.size, borderRadius: '50%',
          background: `radial-gradient(circle, #ffd700, #ff8f00)`,
          boxShadow: '0 0 6px rgba(255,215,0,0.6)',
          animation: `pk-particle ${p.dur}s ease-out ${p.delay}s both`,
          '--px': p.px, '--py': p.py,
        }} />
      ))}
    </div>
  );
}

// ── Helper: find next non-folded, non-allin player (for optimistic turn) ────
function findNextActive(players, fromIdx) {
  for (let i = 1; i < players.length; i++) {
    const idx = (fromIdx + i) % players.length;
    const p = players[idx];
    if (!p.folded && !p.allIn && !p.sittingOut && p.chips > 0) return idx;
  }
  return -1;
}

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
function TexasHoldemPage({ user, userData, onUserDataChange, onBack, isMobile }) {
  const toast = useToast();
  const [gameState, setGameState] = useState(null);
  const [myHand, setMyHand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [pokerCurrency, setPokerCurrency] = useState('crewmate'); // 'crewmate' or 'imposter'
  const [raiseAmount, setRaiseAmount] = useState(2);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const advanceTimerRef = useRef(null);
  const showdownTimerRef = useRef(null);
  const [showTestPanel, setShowTestPanel] = useState(true);
  const [botIds, setBotIds] = useState([]);
  const isTestUser = user && user.email === 'test@test.com';
  const optimisticRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const prevHandNumRef = useRef(null);
  const timerWarnPlayedRef = useRef(false);
  const prevTurnRef = useRef(null);
  const prevCommunityCountRef = useRef(0);

  // Animation states
  const [dealAnim, setDealAnim] = useState(false); // true when new hand just started
  const [communityAnim, setCommunityAnim] = useState(0); // count of newly revealed community cards
  const [showdownAnim, setShowdownAnim] = useState(false);
  const [allInFlash, setAllInFlash] = useState(null); // { userId, ts }
  const [actionBadges, setActionBadges] = useState({}); // { [userId]: { action, amount, ts } }
  const [winnerIds, setWinnerIds] = useState([]); // list of winner userIds for glow
  const [myCardAnim, setMyCardAnim] = useState(false);
  const [potPulse, setPotPulse] = useState(false);
  const [foldAnims, setFoldAnims] = useState({}); // { [userId]: ts }

  // ── Derived state ────────────────────────────────────────────────────────
  const players = gameState?.players || [];
  const myPlayer = players.find(p => p.userId === user.uid);
  const isJoined = !!myPlayer;
  const myIndex = players.findIndex(p => p.userId === user.uid);
  const isMyTurn = gameState?.handInProgress && gameState?.currentPlayerIndex === myIndex && !myPlayer?.folded && !myPlayer?.allIn;
  const phase = gameState?.phase || 'waiting';
  const handInProgress = gameState?.handInProgress || false;

  const totalPot = players.reduce((s, p) => s + (p.totalBet || 0), 0);
  const canCheck = isMyTurn && (myPlayer?.bet || 0) >= (gameState?.currentBet || 0);
  const callAmount = isMyTurn ? Math.min((gameState?.currentBet || 0) - (myPlayer?.bet || 0), myPlayer?.chips || 0) : 0;
  const minRaiseTarget = (gameState?.currentBet || 0) + (gameState?.minRaise || 1);
  const maxRaiseTarget = (myPlayer?.chips || 0) + (myPlayer?.bet || 0);

  // ── Real-time listeners ──────────────────────────────────────────────────
  useEffect(() => {
    const tableRef = doc(db, 'poker', 'mainTable');
    const unsub = onSnapshot(tableRef, (snap) => {
      if (snap.exists()) {
        const s = snap.data();
        // Clear stale optimistic state
        if (optimisticRef.current) {
          const age = Date.now() - optimisticRef.current.appliedAt;
          if (age > 3000 || s.currentPlayerIndex !== optimisticRef.current.snapshot?.currentPlayerIndex) {
            optimisticRef.current = null;
          }
        }

        const newPhase = s.phase;
        const newHandNum = s.handNumber;
        const newCommunityCount = s.communityCards?.length || 0;

        // ── New hand animation ──
        if (prevHandNumRef.current != null && newHandNum > prevHandNumRef.current) {
          SFX.newHand();
          timerWarnPlayedRef.current = false;
          setDealAnim(true);
          setMyCardAnim(true);
          setWinnerIds([]);
          setShowdownAnim(false);
          setFoldAnims({});
          setActionBadges({});
          setTimeout(() => setDealAnim(false), 1200);
          setTimeout(() => setMyCardAnim(false), 800);
          // Play staggered deal sounds
          (s.players || []).forEach((_, i) => SFX.dealCard(i));
        }

        // ── Community card reveal animation ──
        if (newCommunityCount > prevCommunityCountRef.current && prevCommunityCountRef.current >= 0) {
          const newCount = newCommunityCount - prevCommunityCountRef.current;
          setCommunityAnim(newCount);
          if (newPhase === 'flop' || newPhase === 'turn' || newPhase === 'river') SFX.deal();
          setTimeout(() => setCommunityAnim(0), 800);
        }
        prevCommunityCountRef.current = newCommunityCount;

        // ── Showdown / win animation ──
        if (prevPhaseRef.current && prevPhaseRef.current !== newPhase) {
          if (newPhase === 'showdown') {
            setShowdownAnim(true);
            if (s.winners?.length > 0) {
              SFX.win();
              setWinnerIds(s.winners.map(w => w.userId));
              setPotPulse(true);
              setTimeout(() => setPotPulse(false), 600);
            }
            setTimeout(() => setShowdownAnim(false), 2000);
          }
        }

        // ── Other players' action SFX + badges ──
        if (s.lastAction && s.lastAction.userId !== user?.uid) {
          const la = s.lastAction;
          if (la.action === 'fold') {
            SFX.fold();
            setFoldAnims(prev => ({ ...prev, [la.userId]: Date.now() }));
          }
          else if (la.action === 'check') SFX.check();
          else if (la.action === 'call') SFX.call();
          else if (la.action === 'raise') {
            const raiserPlayer = s.players?.find(p => p.userId === la.userId);
            if (raiserPlayer?.allIn || raiserPlayer?.chips === 0) {
              SFX.allIn();
              setAllInFlash({ userId: la.userId, ts: Date.now() });
              setTimeout(() => setAllInFlash(null), 1500);
            } else {
              SFX.raise();
            }
          }
          // Show action badge
          setActionBadges(prev => ({
            ...prev,
            [la.userId]: {
              action: (la.action === 'raise' && (s.players?.find(p => p.userId === la.userId)?.allIn)) ? 'all-in' : la.action,
              amount: la.amount,
              ts: Date.now(),
            }
          }));
          setTimeout(() => {
            setActionBadges(prev => {
              const next = { ...prev };
              delete next[la.userId];
              return next;
            });
          }, 1300);
        }

        // ── Your turn notification ──
        if (prevTurnRef.current !== null && s.currentPlayerIndex !== prevTurnRef.current) {
          const meIdx = (s.players || []).findIndex(p => p.userId === user?.uid);
          if (s.currentPlayerIndex === meIdx && s.handInProgress && s.phase !== 'showdown') {
            SFX.yourTurn();
          }
        }
        prevTurnRef.current = s.currentPlayerIndex;

        prevPhaseRef.current = newPhase;
        prevHandNumRef.current = newHandNum;
        setGameState(s);
      } else {
        setGameState(null);
      }
      setLoading(false);
    }, (err) => {
      console.error('Poker listener error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [user?.uid]);

  // Listen to my private hand
  useEffect(() => {
    if (!user?.uid) return;
    const handRef = doc(db, 'poker', 'mainTable', 'playerHands', user.uid);
    const unsub = onSnapshot(handRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.cards && data.cards.length === 2) {
          setMyHand(data.cards);
        }
      }
    }, () => {});
    return () => unsub();
  }, [user?.uid]);

  // ── Timer countdown + warning sound ──────────────────────────────────
  useEffect(() => {
    if (!gameState?.turnTimerDeadline || !handInProgress || phase === 'showdown') {
      setTimeLeft(30);
      timerWarnPlayedRef.current = false;
      return;
    }
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((gameState.turnTimerDeadline - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 5 && remaining > 0 && isMyTurn && !timerWarnPlayedRef.current) {
        SFX.timerWarn();
        timerWarnPlayedRef.current = true;
      }
      if (remaining > 5) timerWarnPlayedRef.current = false;
    }, 200);
    return () => clearInterval(interval);
  }, [gameState?.turnTimerDeadline, handInProgress, phase, isMyTurn]);

  // ── Auto-advance on timeout ────────────────────────────────────────────
  useEffect(() => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (!gameState?.turnTimerDeadline || !handInProgress || phase === 'showdown' || phase === 'waiting') return;
    const msLeft = gameState.turnTimerDeadline - Date.now() + 1000;
    if (msLeft <= 0) {
      pokerAdvanceFn().catch(() => {});
      return;
    }
    advanceTimerRef.current = setTimeout(() => {
      pokerAdvanceFn().catch(() => {});
    }, msLeft);
    return () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); };
  }, [gameState?.turnTimerDeadline, handInProgress, phase]);

  // ── Auto-advance after showdown ────────────────────────────────────────
  useEffect(() => {
    if (showdownTimerRef.current) clearTimeout(showdownTimerRef.current);
    if (!gameState?.autoNextHandAt || gameState.autoNextHandAt <= 0) return;
    const msLeft = gameState.autoNextHandAt - Date.now() + 500;
    if (msLeft <= 0) {
      pokerAdvanceFn().catch(() => {});
      return;
    }
    showdownTimerRef.current = setTimeout(() => {
      pokerAdvanceFn().catch(() => {});
    }, msLeft);
    return () => { if (showdownTimerRef.current) clearTimeout(showdownTimerRef.current); };
  }, [gameState?.autoNextHandAt]);

  // Reset raise slider when turn changes
  useEffect(() => {
    if (isMyTurn) {
      setRaiseAmount(minRaiseTarget);
      setShowRaiseSlider(false);
    }
  }, [isMyTurn, gameState?.currentPlayerIndex, minRaiseTarget]);

  // ── Actions (optimistic + fire-and-forget) ──────────────────────────────
  const handleAction = useCallback(async (action, raiseTotal) => {
    if (actionPending) return;
    setActionPending(true);

    // Play sound + trigger animations immediately
    if (action === 'fold') {
      SFX.fold();
      setFoldAnims(prev => ({ ...prev, [user.uid]: Date.now() }));
    } else if (action === 'check') {
      SFX.check();
    } else if (action === 'call') {
      SFX.call();
    } else if (action === 'raise') {
      if (raiseTotal >= maxRaiseTarget) {
        SFX.allIn();
        setAllInFlash({ userId: user.uid, ts: Date.now() });
        setTimeout(() => setAllInFlash(null), 1500);
      } else {
        SFX.raise();
      }
    }

    // Show own action badge
    setActionBadges(prev => ({
      ...prev,
      [user.uid]: {
        action: (action === 'raise' && raiseTotal >= maxRaiseTarget) ? 'all-in' : action,
        amount: action === 'call' ? callAmount : (action === 'raise' ? raiseTotal : 0),
        ts: Date.now(),
      }
    }));
    setTimeout(() => {
      setActionBadges(prev => { const n = { ...prev }; delete n[user.uid]; return n; });
    }, 1300);

    // Apply optimistic state overlay
    setGameState(prev => {
      if (!prev) return prev;
      const pIdx = prev.players.findIndex(p => p.userId === user.uid);
      if (pIdx < 0) return prev;
      const updated = { ...prev, players: prev.players.map(p => ({ ...p })) };
      const me = updated.players[pIdx];

      if (action === 'fold') {
        me.folded = true;
      } else if (action === 'check') {
        me.actedThisRound = true;
      } else if (action === 'call') {
        const amt = Math.min((prev.currentBet || 0) - (me.bet || 0), me.chips || 0);
        me.chips = Math.max(0, me.chips - amt);
        me.bet = (me.bet || 0) + amt;
        me.totalBet = (me.totalBet || 0) + amt;
        if (me.chips === 0) me.allIn = true;
      } else if (action === 'raise' && raiseTotal) {
        const amt = raiseTotal - (me.bet || 0);
        me.chips = Math.max(0, me.chips - amt);
        me.bet = raiseTotal;
        me.totalBet = (me.totalBet || 0) + amt;
        updated.currentBet = raiseTotal;
        if (me.chips === 0) me.allIn = true;
      }

      const nextIdx = findNextActive(updated.players, pIdx);
      if (nextIdx >= 0) updated.currentPlayerIndex = nextIdx;
      optimisticRef.current = { action, appliedAt: Date.now(), snapshot: prev };
      return updated;
    });

    setShowRaiseSlider(false);

    try {
      await pokerActionFn({ action, raiseTotal: raiseTotal || null });
    } catch (err) {
      const msg = err?.message || err?.details || 'Action failed';
      toast.error(msg);
      if (optimisticRef.current?.snapshot) {
        setGameState(optimisticRef.current.snapshot);
      }
      optimisticRef.current = null;
    } finally {
      setActionPending(false);
    }
  }, [actionPending, toast, maxRaiseTarget, callAmount, user?.uid]);

  const handleJoin = useCallback(async () => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await joinTableFn({ currencyType: pokerCurrency });
      toast.success(pokerCurrency === 'imposter' ? 'Joined with 2.5🔴 → 10 chips!' : 'Joined the table!');
      onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to join');
    } finally {
      setActionPending(false);
    }
  }, [actionPending, toast, onUserDataChange, pokerCurrency]);

  const handleLeave = useCallback(async () => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await leaveTableFn();
      setMyHand(null);
      toast.success('Left the table');
      onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to leave');
    } finally {
      setActionPending(false);
    }
  }, [actionPending, toast, onUserDataChange]);

  const handleRebuy = useCallback(async () => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await pokerRebuyFn({ currencyType: pokerCurrency });
      toast.success(pokerCurrency === 'imposter' ? 'Re-bought with 2.5🔴 → 10 chips!' : 'Re-bought for 10🔵 → 10 chips!');
      onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to re-buy');
    } finally {
      setActionPending(false);
    }
  }, [actionPending, toast, onUserDataChange, pokerCurrency]);

  // ── Test helpers ───────────────────────────────────────────────────────
  const handleAddBot = useCallback(async (name) => {
    try {
      const res = await testAddBotFn({ botName: name || undefined });
      const { botId, botName } = res.data;
      setBotIds(prev => [...prev, { id: botId, name: botName }]);
      toast.success(`Added ${botName}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to add bot');
    }
  }, [toast]);

  const handleBotAction = useCallback(async (botId, action, raiseTotal) => {
    if (action === 'fold') SFX.fold();
    else if (action === 'check') SFX.check();
    else if (action === 'call') SFX.call();
    else if (action === 'raise') SFX.raise();

    setActionBadges(prev => ({
      ...prev,
      [botId]: { action, amount: raiseTotal || 0, ts: Date.now() }
    }));
    setTimeout(() => {
      setActionBadges(prev => { const n = { ...prev }; delete n[botId]; return n; });
    }, 1300);

    setGameState(prev => {
      if (!prev) return prev;
      const pIdx = prev.players.findIndex(p => p.userId === botId);
      if (pIdx < 0) return prev;
      const updated = { ...prev, players: prev.players.map(p => ({ ...p })) };
      const bot = updated.players[pIdx];
      if (action === 'fold') bot.folded = true;
      else if (action === 'call') {
        const amt = Math.min((prev.currentBet || 0) - (bot.bet || 0), bot.chips || 0);
        bot.chips = Math.max(0, bot.chips - amt);
        bot.bet = (bot.bet || 0) + amt;
        bot.totalBet = (bot.totalBet || 0) + amt;
        if (bot.chips === 0) bot.allIn = true;
      } else if (action === 'raise' && raiseTotal) {
        const amt = raiseTotal - (bot.bet || 0);
        bot.chips = Math.max(0, bot.chips - amt);
        bot.bet = raiseTotal;
        bot.totalBet = (bot.totalBet || 0) + amt;
        updated.currentBet = raiseTotal;
        if (bot.chips === 0) bot.allIn = true;
      }
      const nextIdx = findNextActive(updated.players, pIdx);
      if (nextIdx >= 0) updated.currentPlayerIndex = nextIdx;
      return updated;
    });

    try {
      await testBotActionFn({ botId, action, raiseTotal: raiseTotal || null });
    } catch (err) {
      toast.error(err?.message || 'Bot action failed');
    }
  }, [toast]);

  const handleRemoveBot = useCallback(async (botId) => {
    try {
      await testRemoveBotFn({ botId });
      setBotIds(prev => prev.filter(b => b.id !== botId));
      toast.success('Bot removed');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove bot');
    }
  }, [toast]);

  const handleReset = useCallback(async () => {
    try {
      await testResetFn();
      setBotIds([]);
      setMyHand(null);
      toast.success('Table reset');
      onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to reset');
    }
  }, [toast, onUserDataChange]);

  // ── Loading ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading poker table...
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────
  const communityCards = gameState?.communityCards || [];
  const winners = gameState?.winners || [];
  const revealedHands = gameState?.revealedHands || {};
  const lastAction = gameState?.lastAction;

  return (
    <div style={{
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0,
      left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0,
      bottom: isMobile ? undefined : 0,
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-primary)',
      overflow: isMobile ? 'auto' : 'hidden',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '80px' : 0,
    }}>
      {/* All-in screen flash */}
      {allInFlash && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'radial-gradient(ellipse at center, rgba(255,140,0,0.15), transparent 70%)',
          animation: 'pk-screenFlash 0.6s ease-out both',
          pointerEvents: 'none',
        }} />
      )}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '8px 12px' : '12px 20px', flexShrink: 0,
        gap: isMobile ? 8 : undefined, flexWrap: isMobile ? 'wrap' : undefined,
      }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, padding: '8px 16px', color: 'var(--text-primary)',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          transition: 'all 0.2s',
        }}>
          &larr; Casino
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: 1,
            background: 'linear-gradient(90deg, #fff, #ffd700, #fff)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            animation: phase === 'showdown' && winners.length > 0 ? 'pk-shimmer 2s linear infinite' : 'none',
          }}>
            Texas Hold'em
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            Blinds: 0.25 / 0.5 &mdash; Buy-in: 10🔵 or 2.5🔴
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isJoined && (
            <div style={{
              background: 'rgba(34,139,34,0.15)', border: '1px solid rgba(34,139,34,0.4)',
              borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 700,
              color: '#4caf50', transition: 'all 0.3s',
            }}>
              {myPlayer?.chips?.toFixed(2)} chips {myPlayer?.currencyType === 'imposter' ? '🔴' : '🔵'}
            </div>
          )}
          {isJoined ? (
            <button onClick={handleLeave} disabled={actionPending} style={{
              background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.4)',
              borderRadius: 8, padding: '8px 14px', color: '#f44336',
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
              transition: 'all 0.2s',
            }}>
              Leave
            </button>
          ) : null}
        </div>
      </div>

      {/* Main table area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Poker table */}
        <div style={{
          flex: isMobile ? undefined : 1, position: 'relative', margin: '0 auto',
          minHeight: isMobile ? '340px' : 0, width: '100%', maxWidth: 900,
          aspectRatio: isMobile ? '4/3' : undefined,
        }}>
          {/* Felt table */}
          <div style={{
            position: 'absolute', inset: isMobile ? '3% 2%' : '5% 5%',
            borderRadius: '50%',
            background: 'radial-gradient(ellipse at 50% 40%, #1b5e20 0%, #0d3b12 60%, #092408 100%)',
            border: isMobile ? '4px solid #2e1a08' : '6px solid #2e1a08',
            boxShadow: isMobile
              ? 'inset 0 0 30px rgba(0,0,0,0.5), 0 2px 15px rgba(0,0,0,0.6)'
              : 'inset 0 0 60px rgba(0,0,0,0.5), 0 4px 30px rgba(0,0,0,0.6), 0 0 0 10px #1a0e04',
          }}>
            {/* Table felt texture overlay */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.01) 2px, rgba(255,255,255,0.01) 4px)',
            }} />

            {/* Center content: pot + community cards */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 4 : 8,
              zIndex: 2, width: isMobile ? '90%' : '80%', maxWidth: isMobile ? 280 : 400,
            }}>
              {/* Pot */}
              {totalPot > 0 && (
                <div style={{
                  background: 'rgba(0,0,0,0.5)', borderRadius: 20,
                  padding: '4px 14px', fontSize: 13, fontWeight: 700,
                  color: '#ffd700', whiteSpace: 'nowrap',
                  border: '1px solid rgba(255,215,0,0.3)',
                  animation: potPulse ? 'pk-potPulse 0.4s ease' : 'none',
                  transition: 'all 0.3s',
                }}>
                  Pot: {totalPot.toFixed(2)}
                </div>
              )}

              {/* Community cards with flip animation */}
              <div style={{ display: 'flex', gap: isMobile ? 3 : 6 }}>
                {communityCards.map((c, i) => {
                  const isNew = communityAnim > 0 && i >= communityCards.length - communityAnim;
                  return (
                    <CardView
                      key={`cc-${i}`}
                      card={c}
                      large
                      animClass={isNew ? 'pk-cardFlip' : undefined}
                      animDelay={isNew ? `${(i - (communityCards.length - communityAnim)) * 0.12}s` : undefined}
                      animDuration="0.5s"
                    />
                  );
                })}
                {/* Placeholder slots */}
                {phase !== 'waiting' && Array.from({ length: Math.max(0, 5 - communityCards.length) }).map((_, i) => (
                  <div key={`empty-${i}`} style={{
                    width: isMobile ? 36 : 62, height: isMobile ? 52 : 88, borderRadius: isMobile ? 4 : 6,
                    border: '1px dashed rgba(255,255,255,0.08)',
                    transition: 'all 0.3s',
                  }} />
                ))}
              </div>

              {/* Phase indicator */}
              {handInProgress && phase !== 'waiting' && (
                <div style={{
                  fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase', letterSpacing: 2,
                  animation: 'pk-fadeIn 0.3s ease',
                }}>
                  {phase}
                </div>
              )}

              {/* Winners display with fanfare — centered below community cards */}
              {phase === 'showdown' && winners.length > 0 && (
                <div style={{
                  background: 'rgba(0,0,0,0.75)', borderRadius: 14, padding: '10px 20px',
                  textAlign: 'center', alignSelf: 'center',
                  border: '2px solid rgba(255,215,0,0.6)',
                  boxShadow: '0 0 30px rgba(255,215,0,0.2), 0 0 60px rgba(255,215,0,0.1)',
                  animation: 'pk-winBanner 0.6s ease both',
                  position: 'relative', overflow: 'visible',
                  whiteSpace: 'nowrap',
                }}>
                  <GoldParticles active={showdownAnim} />
                  {winners.map((w, i) => (
                    <div key={i} style={{
                      fontSize: 14, color: '#ffd700', fontWeight: 700,
                      textShadow: '0 0 10px rgba(255,215,0,0.5)',
                    }}>
                      {w.username} wins {w.amount.toFixed(2)}
                      {w.handName && <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}> &mdash; {w.handName}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Waiting message */}
              {phase === 'waiting' && (
                <div style={{
                  fontSize: 14, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textAlign: 'center',
                  animation: 'pk-fadeIn 0.4s ease',
                }}>
                  {players.filter(p => p.chips > 0 && !p.leaving).length < 3
                    ? `Waiting for players (${players.filter(p => p.chips > 0 && !p.leaving).length}/3)...`
                    : 'Starting soon...'}
                </div>
              )}
            </div>

            {/* Players around the table */}
            {players.map((p, i) => {
              const pos = getPlayerPosition(i, players.length, myIndex);
              const isCurrent = handInProgress && gameState?.currentPlayerIndex === i && phase !== 'showdown';
              const isDealer = gameState?.dealerIndex === i;
              const isSB = gameState?.sbIndex === i;
              const isBB = gameState?.bbIndex === i;
              const revealed = revealedHands[p.userId];
              const isMe = p.userId === user.uid;
              const isWinner = winnerIds.includes(p.userId);
              const isAllInFlashing = allInFlash?.userId === p.userId;
              const badge = actionBadges[p.userId];
              const isFoldAnimating = foldAnims[p.userId] && Date.now() - foldAnims[p.userId] < 600;

              return (
                <div key={p.userId} style={{
                  position: 'absolute',
                  left: pos.left, top: pos.top,
                  transform: 'translate(-50%, -50%)',
                  zIndex: isCurrent ? 10 : isWinner ? 9 : 5,
                  pointerEvents: 'none',
                  transition: 'all 0.3s ease',
                }}>
                  {/* Action badge */}
                  <ActionBadge action={badge?.action} amount={badge?.amount} visible={!!badge} />

                  {/* Bet amount with chip animation */}
                  {p.bet > 0 && handInProgress && (
                    <div style={{
                      position: 'absolute', left: '50%', bottom: -6,
                      transform: 'translateX(-50%) translateY(100%)',
                      background: 'rgba(0,0,0,0.8)', borderRadius: 12,
                      padding: '4px 12px', fontSize: 14, fontWeight: 700,
                      color: '#ffd700', whiteSpace: 'nowrap',
                      animation: 'pk-chipSlide 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,215,0,0.3)',
                      textShadow: '0 0 6px rgba(255,215,0,0.3)',
                    }}>
                      Bet: {p.bet.toFixed(2)}
                    </div>
                  )}

                  {/* Player card */}
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 2 : 4,
                    padding: isMobile ? '4px 6px' : '6px 10px', borderRadius: isMobile ? 8 : 12,
                    background: isCurrent
                      ? 'rgba(76,175,80,0.2)'
                      : isWinner ? 'rgba(255,215,0,0.12)'
                      : p.folded ? 'rgba(0,0,0,0.5)'
                      : isAllInFlashing ? 'rgba(255,111,0,0.2)'
                      : 'rgba(0,0,0,0.35)',
                    border: isCurrent
                      ? '2px solid rgba(76,175,80,0.7)'
                      : isWinner ? '2px solid rgba(255,215,0,0.6)'
                      : isAllInFlashing ? '2px solid rgba(255,111,0,0.7)'
                      : '1px solid rgba(255,255,255,0.1)',
                    opacity: p.folded && !isFoldAnimating ? 0.4 : p.sittingOut ? 0.4 : 1,
                    minWidth: 70,
                    transition: 'background 0.3s, border 0.3s, opacity 0.4s',
                    animation: isCurrent ? 'pk-turnPulse 1.5s ease-in-out infinite'
                      : isWinner ? 'pk-winGlow 1s ease-in-out infinite'
                      : isAllInFlashing ? 'pk-allInPulse 0.5s ease-in-out 3'
                      : dealAnim ? `pk-dealIn 0.5s ease ${i * 0.08}s both`
                      : 'none',
                  }}>
                    {/* Cards (face down for others, revealed at showdown) */}
                    {!p.sittingOut && !p.folded && handInProgress && phase !== 'showdown' && (
                      <div style={{
                        display: 'flex', gap: 2,
                        animation: isFoldAnimating ? 'pk-foldOut 0.5s ease-out both' : undefined,
                      }}>
                        {isMe && myHand ? (
                          <>
                            <CardView card={myHand[0]} animClass={dealAnim ? 'pk-dealIn' : undefined} animDelay="0.1s" />
                            <CardView card={myHand[1]} animClass={dealAnim ? 'pk-dealIn' : undefined} animDelay="0.2s" />
                          </>
                        ) : (
                          <>
                            <CardView faceDown animClass={dealAnim ? 'pk-dealIn' : undefined} animDelay={`${0.1 + i * 0.05}s`} />
                            <CardView faceDown animClass={dealAnim ? 'pk-dealIn' : undefined} animDelay={`${0.15 + i * 0.05}s`} />
                          </>
                        )}
                      </div>
                    )}

                    {/* Showdown revealed cards with flip */}
                    {phase === 'showdown' && revealed && (
                      <div style={{ display: 'flex', gap: 2 }}>
                        <CardView card={revealed.cards[0]} animClass={showdownAnim ? 'pk-cardFlip' : undefined} animDelay="0s" animDuration="0.5s" />
                        <CardView card={revealed.cards[1]} animClass={showdownAnim ? 'pk-cardFlip' : undefined} animDelay="0.15s" animDuration="0.5s" />
                      </div>
                    )}

                    {/* Name + chips */}
                    <div style={{
                      fontSize: 11, fontWeight: 700,
                      color: isMe ? '#4caf50' : isWinner ? '#ffd700' : '#fff',
                      whiteSpace: 'nowrap', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis',
                      textAlign: 'center',
                      transition: 'color 0.3s',
                    }}>
                      {p.username}
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 700,
                      color: p.allIn ? '#ff6f00' : '#ffd700',
                      transition: 'all 0.3s',
                      textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                    }}>
                      {p.sittingOut ? 'Sitting Out' : p.folded ? 'Folded' : p.allIn ? (
                        <span style={{ fontWeight: 800, letterSpacing: 1, textShadow: '0 0 6px rgba(255,111,0,0.5)' }}>ALL IN</span>
                      ) : `${p.chips.toFixed(2)}`}
                    </div>

                    {/* Badges */}
                    <div style={{ display: 'flex', gap: 3 }}>
                      {isDealer && (
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', fontSize: 9, fontWeight: 800,
                          background: 'linear-gradient(135deg, #ffd700, #ff8f00)', color: '#000',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(255,215,0,0.4)',
                        }}>D</div>
                      )}
                      {isSB && (
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', fontSize: 8, fontWeight: 800,
                          background: 'rgba(33,150,243,0.8)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>SB</div>
                      )}
                      {isBB && (
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', fontSize: 8, fontWeight: 800,
                          background: 'rgba(255,152,0,0.8)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>BB</div>
                      )}
                    </div>

                    {/* Timer bar for current player */}
                    {isCurrent && gameState?.turnTimerDeadline && (
                      <TimerBar deadline={gameState.turnTimerDeadline} />
                    )}

                    {/* Showdown hand name */}
                    {phase === 'showdown' && revealed && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, color: '#ffd700',
                        background: 'rgba(0,0,0,0.5)', borderRadius: 6, padding: '2px 6px',
                        animation: 'pk-fadeIn 0.4s ease 0.3s both',
                        textShadow: '0 0 4px rgba(255,215,0,0.3)',
                      }}>
                        {revealed.handName}
                      </div>
                    )}

                    {/* Winner particles */}
                    {isWinner && showdownAnim && <GoldParticles active={true} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom panel: my hand + actions */}
        <div style={{
          flexShrink: 0, padding: '10px 16px 14px',
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.4))',
          position: 'relative',
        }}>
          {!isJoined ? (
            /* Join panel */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, animation: 'pk-fadeIn 0.4s ease' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                {players.length} player{players.length !== 1 ? 's' : ''} at the table
              </div>
              {/* Currency toggle */}
              <div style={{ display: 'flex', gap: 6 }}>
                {['crewmate', 'imposter'].map(ct => (
                  <button key={ct} onClick={() => setPokerCurrency(ct)} style={{
                    padding: '6px 16px', borderRadius: 8, border: `2px solid ${pokerCurrency === ct ? (ct === 'crewmate' ? '#42a5f5' : '#ef5350') : 'rgba(255,255,255,0.15)'}`,
                    background: pokerCurrency === ct ? (ct === 'crewmate' ? 'rgba(66,165,245,0.15)' : 'rgba(239,83,80,0.15)') : 'transparent',
                    color: pokerCurrency === ct ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                    {ct === 'crewmate' ? '🔵 10 Crew' : '🔴 2.5 Imp'}
                  </button>
                ))}
              </div>
              {(() => {
                const canAfford = pokerCurrency === 'imposter'
                  ? (userData?.imposterTokens || 0) >= 2.5
                  : (userData?.crewmateTokens || 0) >= 10;
                return <>
                  <button onClick={handleJoin} disabled={actionPending || !canAfford} style={{
                    padding: '12px 32px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(46,125,50,0.4)',
                    opacity: actionPending || !canAfford ? 0.5 : 1,
                    transition: 'all 0.2s',
                  }}>
                    {actionPending ? 'Joining...' : pokerCurrency === 'imposter' ? 'Join Table (2.5🔴 → 10 chips)' : 'Join Table (10🔵 → 10 chips)'}
                  </button>
                  {!canAfford && (
                    <div style={{ fontSize: 12, color: '#f44336' }}>
                      Need at least {pokerCurrency === 'imposter' ? '2.5 impostor' : '10 crewmate'} tokens
                    </div>
                  )}
                </>;
              })()}
            </div>
          ) : (
            /* Player controls — 3-column layout (stacked on mobile) */
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', gap: isMobile ? 8 : 12 }}>

              {/* Left: Hand Rankings — hidden on mobile to save space */}
              <div style={{
                position: 'absolute', left: 16, bottom: 14,
                fontSize: 12, lineHeight: 1.7,
                background: 'rgba(0,0,0,0.65)', borderRadius: 10, padding: '8px 12px',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap',
                zIndex: 5, backdropFilter: 'blur(8px)',
                display: isMobile ? 'none' : undefined,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4, letterSpacing: 0.5 }}>Hand Rankings</div>
                <div style={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>A K Q J 10 <span style={{ color: '#ef5350' }}>{'\u2665'}</span></span> Royal Flush</div>
                <div style={{ color: '#e0c060', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>5 6 7 8 9 <span style={{ color: '#42a5f5' }}>{'\u2666'}</span></span> Straight Flush</div>
                <div style={{ color: '#c0a040', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>K K K K</span> Four of a Kind</div>
                <div style={{ color: '#b0b0b0', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>J J J 5 5</span> Full House</div>
                <div style={{ color: '#9090a0', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>2 6 9 J A <span style={{ color: '#66bb6a' }}>{'\u2663'}</span></span> Flush</div>
                <div style={{ color: '#808090', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>4 5 6 7 8</span> Straight</div>
                <div style={{ color: '#707080', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>Q Q Q</span> Three of a Kind</div>
                <div style={{ color: '#606070', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>9 9 4 4</span> Two Pair</div>
                <div style={{ color: '#555565', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>A A</span> One Pair</div>
                <div style={{ color: '#4a4a58', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 10, opacity: 0.7 }}>K {'\u2660'}</span> High Card</div>
              </div>

              {/* Center: My cards + status messages */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {/* My cards */}
                {handInProgress && myHand && !myPlayer?.sittingOut && (
                  <div style={{
                    display: 'flex', justifyContent: 'center', gap: 6,
                    animation: myCardAnim ? 'pk-myCardReveal 0.6s ease both' : undefined,
                  }}>
                    <CardView card={myHand[0]} large />
                    <CardView card={myHand[1]} large />
                    {phase === 'showdown' && revealedHands[user.uid] && (
                      <div style={{
                        alignSelf: 'center', marginLeft: 8,
                        background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)',
                        borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: '#ffd700',
                        animation: 'pk-fadeIn 0.4s ease 0.2s both',
                        textShadow: '0 0 6px rgba(255,215,0,0.3)',
                      }}>
                        {revealedHands[user.uid].handName}
                      </div>
                    )}
                  </div>
                )}

                {/* Sitting out */}
                {myPlayer?.sittingOut && !myPlayer?.leaving && myPlayer?.chips > 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                    Sitting out &mdash; you'll be dealt in next hand
                  </div>
                )}

                {/* Buy back in */}
                {myPlayer && myPlayer.chips <= 0 && !myPlayer.leaving && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    padding: '12px 20px', borderRadius: 12,
                    background: 'rgba(21,101,192,0.1)', border: '1px solid rgba(21,101,192,0.3)',
                    animation: 'pk-fadeIn 0.4s ease',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Out of chips!</div>
                    {/* Currency toggle for rebuy */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['crewmate', 'imposter'].map(ct => (
                        <button key={ct} onClick={() => setPokerCurrency(ct)} style={{
                          padding: '4px 12px', borderRadius: 6, border: `2px solid ${pokerCurrency === ct ? (ct === 'crewmate' ? '#42a5f5' : '#ef5350') : 'rgba(255,255,255,0.15)'}`,
                          background: pokerCurrency === ct ? (ct === 'crewmate' ? 'rgba(66,165,245,0.15)' : 'rgba(239,83,80,0.15)') : 'transparent',
                          color: pokerCurrency === ct ? '#fff' : 'rgba(255,255,255,0.5)',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                        }}>
                          {ct === 'crewmate' ? '🔵 10 Crew' : '🔴 2.5 Imp'}
                        </button>
                      ))}
                    </div>
                    {(() => {
                      const canAfford = pokerCurrency === 'imposter'
                        ? (userData?.imposterTokens || 0) >= 2.5
                        : (userData?.crewmateTokens || 0) >= 10;
                      return <>
                        <button onClick={handleRebuy} disabled={actionPending || !canAfford} style={{
                          padding: '10px 24px', borderRadius: 10, border: 'none',
                          background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                          color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                          boxShadow: '0 4px 15px rgba(21,101,192,0.4)',
                          opacity: actionPending || !canAfford ? 0.5 : 1,
                          transition: 'all 0.2s',
                        }}>
                          {actionPending ? 'Processing...' : pokerCurrency === 'imposter' ? 'Buy Back In (2.5🔴)' : 'Buy Back In (10🔵)'}
                        </button>
                        {!canAfford && (
                          <div style={{ fontSize: 11, color: '#f44336' }}>
                            Need at least {pokerCurrency === 'imposter' ? '2.5 impostor' : '10 crewmate'} tokens
                          </div>
                        )}
                      </>;
                    })()}
                  </div>
                )}

                {/* Last action indicator */}
                {lastAction && handInProgress && !isMyTurn && (
                  <div style={{
                    textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)',
                    animation: 'pk-fadeIn 0.3s ease',
                  }}>
                    {lastAction.username} {lastAction.auto ? 'timed out \u2014 ' : ''}{lastAction.action}
                    {lastAction.amount > 0 ? ` ${lastAction.amount.toFixed(2)}` : ''}
                  </div>
                )}

                {/* Waiting indicator */}
                {handInProgress && !isMyTurn && !myPlayer?.folded && !myPlayer?.sittingOut && !myPlayer?.allIn && phase !== 'showdown' && (
                  <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>
                    Waiting for {players[gameState?.currentPlayerIndex]?.username || 'player'}...
                  </div>
                )}
              </div>

              {/* Right: Action controls */}
              <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                {isMyTurn && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: 0,
                    animation: 'pk-fadeIn 0.25s ease',
                    borderRadius: 14,
                    overflow: 'hidden',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
                    minWidth: isMobile ? 0 : 270,
                    width: isMobile ? '100%' : undefined,
                  }}>
                    {/* Timer bar */}
                    <div style={{
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      background: timeLeft <= 5 ? 'rgba(244,67,54,0.15)' : 'rgba(255,255,255,0.03)',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1 }}>Your Turn</span>
                      <span style={{
                        fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: timeLeft <= 5 ? '#f44336' : timeLeft <= 10 ? '#ff9800' : 'rgba(255,255,255,0.7)',
                        animation: timeLeft <= 5 ? 'pk-potPulse 0.5s ease infinite' : 'none',
                      }}>
                        {timeLeft}s
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', background: 'rgba(0,0,0,0.4)' }}>
                      <button
                        onClick={() => handleAction('fold')}
                        disabled={actionPending}
                        style={{
                          flex: 1, padding: '14px 0', border: 'none', borderRight: '1px solid rgba(255,255,255,0.06)',
                          background: 'transparent',
                          color: actionPending ? 'rgba(255,255,255,0.3)' : '#ef5350', fontSize: 15, fontWeight: 600,
                          cursor: actionPending ? 'default' : 'pointer',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!actionPending) e.target.style.background = 'rgba(239,83,80,0.12)'; }}
                        onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                      >
                        Fold
                      </button>

                      {canCheck ? (
                        <button
                          onClick={() => handleAction('check')}
                          disabled={actionPending}
                          style={{
                            flex: 1, padding: '14px 0', border: 'none',
                            background: 'transparent',
                            color: actionPending ? 'rgba(255,255,255,0.3)' : '#64b5f6', fontSize: 15, fontWeight: 600,
                            cursor: actionPending ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (!actionPending) e.target.style.background = 'rgba(100,181,246,0.12)'; }}
                          onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                        >
                          Check
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAction('call')}
                          disabled={actionPending}
                          style={{
                            flex: 1, padding: '14px 0', border: 'none',
                            background: 'transparent',
                            color: actionPending ? 'rgba(255,255,255,0.3)' : '#64b5f6', fontSize: 15, fontWeight: 600,
                            cursor: actionPending ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (!actionPending) e.target.style.background = 'rgba(100,181,246,0.12)'; }}
                          onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                        >
                          Call <span style={{ opacity: 0.7, fontWeight: 400 }}>{callAmount.toFixed(2)}</span>
                        </button>
                      )}
                    </div>

                    {/* Raise section */}
                    {maxRaiseTarget > (gameState?.currentBet || 0) && (myPlayer?.chips || 0) > 0 && (
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.35)' }}>
                        {showRaiseSlider ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 6 }}>
                              <button onClick={() => setRaiseAmount(a => Math.max(minRaiseTarget, parseFloat((a - 0.5).toFixed(2))))} style={{
                                width: 34, height: 34, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', fontSize: 18,
                                fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.04)'; }}
                              >&minus;</button>
                              <button
                                onClick={() => handleAction('raise', raiseAmount)}
                                disabled={actionPending}
                                style={{
                                  flex: 1, padding: '10px 0', borderRadius: 8,
                                  border: '1px solid rgba(76,175,80,0.3)',
                                  background: 'rgba(76,175,80,0.15)',
                                  color: '#81c784', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                                  transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { e.target.style.background = 'rgba(76,175,80,0.25)'; }}
                                onMouseLeave={e => { e.target.style.background = 'rgba(76,175,80,0.15)'; }}
                              >
                                Raise to {raiseAmount.toFixed(2)}
                              </button>
                              <button onClick={() => setRaiseAmount(a => Math.min(maxRaiseTarget, parseFloat((a + 0.5).toFixed(2))))} style={{
                                width: 34, height: 34, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', fontSize: 18,
                                fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.04)'; }}
                              >+</button>
                            </div>
                            <button
                              onClick={() => handleAction('raise', maxRaiseTarget)}
                              disabled={actionPending}
                              style={{
                                padding: '10px 0', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)',
                                background: 'transparent',
                                color: actionPending ? 'rgba(255,255,255,0.3)' : '#ffb74d', fontSize: 13, fontWeight: 600,
                                cursor: actionPending ? 'default' : 'pointer',
                                transition: 'background 0.15s',
                                borderRadius: '0 0 14px 14px',
                              }}
                              onMouseEnter={e => { if (!actionPending) e.target.style.background = 'rgba(255,183,77,0.12)'; }}
                              onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                            >
                              All In &middot; {maxRaiseTarget.toFixed(2)}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex' }}>
                            <button
                              onClick={() => setShowRaiseSlider(true)}
                              style={{
                                flex: 1, padding: '12px 0', border: 'none', borderRight: '1px solid rgba(255,255,255,0.06)',
                                background: 'transparent',
                                color: '#81c784', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => { e.target.style.background = 'rgba(76,175,80,0.12)'; }}
                              onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                            >
                              Raise
                            </button>
                            <button
                              onClick={() => handleAction('raise', maxRaiseTarget)}
                              disabled={actionPending}
                              style={{
                                flex: 1, padding: '12px 0', border: 'none',
                                background: 'transparent',
                                color: actionPending ? 'rgba(255,255,255,0.3)' : '#ffb74d', fontSize: 15, fontWeight: 600,
                                cursor: actionPending ? 'default' : 'pointer',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => { if (!actionPending) e.target.style.background = 'rgba(255,183,77,0.12)'; }}
                              onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                            >
                              All In
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Test Panel ──────────────────────────────────────────────────── */}
      {isTestUser && (
        <div style={{
          position: 'fixed', bottom: 0, right: 0, zIndex: 1000,
          width: showTestPanel ? 380 : 'auto',
        }}>
          <button
            onClick={() => setShowTestPanel(!showTestPanel)}
            style={{
              background: '#ff5722', color: '#fff', border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'block', marginLeft: 'auto', marginRight: 8,
            }}
          >
            {showTestPanel ? 'Close Test Panel' : 'Open Test Panel'}
          </button>

          {showTestPanel && (
            <div style={{
              background: '#1a1a1a', border: '1px solid #ff5722', borderRadius: '8px 0 0 0',
              padding: 12, overflowY: 'auto', maxHeight: '70vh', fontSize: 12,
            }}>
              <div style={{ fontWeight: 700, color: '#ff5722', marginBottom: 8 }}>Test Controls</div>

              {/* Reset + Add Bot */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <button onClick={() => handleAddBot()} style={testBtnStyle('#4caf50')}>+ Add Bot</button>
                <button onClick={() => handleAddBot('Alice')} style={testBtnStyle('#2196f3')}>+ Alice</button>
                <button onClick={() => handleAddBot('Bob')} style={testBtnStyle('#2196f3')}>+ Bob</button>
                <button onClick={() => handleAddBot('Charlie')} style={testBtnStyle('#2196f3')}>+ Charlie</button>
                <button onClick={handleReset} style={testBtnStyle('#f44336')}>Reset Table</button>
              </div>

              {/* Current game info */}
              <div style={{ color: '#aaa', marginBottom: 8, fontSize: 11 }}>
                Phase: {phase} | Hand: {gameState?.handNumber || 0} | Players: {players.length}
                {gameState?.currentPlayerIndex >= 0 && (
                  <span> | Turn: {players[gameState.currentPlayerIndex]?.username}</span>
                )}
              </div>

              {/* Bot list with action buttons */}
              {players.filter(p => p.isBot).map(bot => {
                const isBotTurn = handInProgress && players[gameState?.currentPlayerIndex]?.userId === bot.userId;
                const botCallAmt = Math.min((gameState?.currentBet || 0) - (bot.bet || 0), bot.chips || 0);
                const botCanCheck = (bot.bet || 0) >= (gameState?.currentBet || 0);

                return (
                  <div key={bot.userId} style={{
                    padding: '6px 8px', marginBottom: 4, borderRadius: 6,
                    background: isBotTurn ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.04)',
                    border: isBotTurn ? '1px solid rgba(76,175,80,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ color: '#fff', fontWeight: 600 }}>
                        {bot.username} {isBotTurn ? '(TURN)' : ''}
                      </span>
                      <span style={{ color: '#aaa', fontSize: 10 }}>
                        {bot.chips?.toFixed(2)} chips | bet: {bot.bet?.toFixed(2)}
                        {bot.folded ? ' | FOLDED' : ''}{bot.allIn ? ' | ALL-IN' : ''}
                      </span>
                    </div>

                    {revealedHands[bot.userId] && (
                      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                        {revealedHands[bot.userId].cards.map((c, ci) => (
                          <CardView key={ci} card={c} />
                        ))}
                        <span style={{ fontSize: 10, color: '#ffd700', alignSelf: 'center', marginLeft: 4 }}>
                          {revealedHands[bot.userId].handName}
                        </span>
                      </div>
                    )}

                    {isBotTurn && !bot.folded && !bot.allIn && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button onClick={() => handleBotAction(bot.userId, 'fold')} style={testBtnStyle('#f44336')}>Fold</button>
                        {botCanCheck ? (
                          <button onClick={() => handleBotAction(bot.userId, 'check')} style={testBtnStyle('#2196f3')}>Check</button>
                        ) : (
                          <button onClick={() => handleBotAction(bot.userId, 'call')} style={testBtnStyle('#2196f3')}>
                            Call {botCallAmt.toFixed(2)}
                          </button>
                        )}
                        <button onClick={() => handleBotAction(bot.userId, 'raise', (gameState?.currentBet || 0) + (gameState?.minRaise || 1))} style={testBtnStyle('#4caf50')}>
                          Min Raise
                        </button>
                        <button onClick={() => handleBotAction(bot.userId, 'raise', (bot.chips || 0) + (bot.bet || 0))} style={testBtnStyle('#ff9800')}>
                          All In
                        </button>
                        <button onClick={() => handleRemoveBot(bot.userId)} style={testBtnStyle('#666')}>Remove</button>
                      </div>
                    )}

                    {!isBotTurn && !bot.folded && !bot.allIn && !bot.sittingOut && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleRemoveBot(bot.userId)} style={testBtnStyle('#666')}>Remove</button>
                      </div>
                    )}
                  </div>
                );
              })}

              {players.filter(p => p.isBot).length === 0 && (
                <div style={{ color: '#666', fontSize: 11 }}>No bots at the table. Add some to test.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function testBtnStyle(bg) {
  return {
    padding: '3px 8px', borderRadius: 4, border: 'none',
    background: bg, color: '#fff', fontSize: 10, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

export default TexasHoldemPage;
